import { createEntityAdapter, createSelector, createSlice, EntityState, PayloadAction } from '@reduxjs/toolkit'
import { AiFeature } from '../../models/AiFeature'
import { RootState } from '../store'
import { Scorecard } from '../../models/Scorecard'
import { AIAgentResult } from '../../models/AIAgent'
import { selectSelectedMeetingTemplate } from './meetingTemplates'
import { selectUsersWithClientRole } from './roomSession'
import { hasTenMinutesPassed } from '../utils'
import { RoomEvent } from '../media/media'
import { selectExecutedAiFeatures } from './executedAiFeatures'

const MEETING_LEVEL_RESULT_USER_ID = 'None'

interface FormsState {
  formAiFeatures: EntityState<AiFeature, string>
  formScorecards: EntityState<Scorecard, string>
  formAiAgentResults: EntityState<AIAgentResult, string>
  formEventsForPolling: RoomEvent[]
  formAiFeaturesLoading: boolean
  formEventsForPollingLoading: boolean
  formScorecardsLoading: boolean
  formAiAgentResultsLoading: boolean
  formEventsPollingEnabled: boolean
  pollingForms: string[]
  executingForms: string[]
  formFillStartedFrom: string | null
}

const formAiFeaturesAdapter = createEntityAdapter<AiFeature>()
const formScorecardsAdapter = createEntityAdapter<Scorecard>()
const formAiAgentResultsAdapter = createEntityAdapter<AIAgentResult, string>({ selectId: (aiAgentResult: AIAgentResult) => aiAgentResult.resultKey })

const initialState: FormsState = {
  formAiFeatures: formAiFeaturesAdapter.getInitialState(),
  formScorecards: formScorecardsAdapter.getInitialState(),
  formAiAgentResults: formAiAgentResultsAdapter.getInitialState(),
  formEventsForPolling: [],
  formAiFeaturesLoading: true,
  formEventsForPollingLoading: true,
  formScorecardsLoading: true,
  formAiAgentResultsLoading: true,
  formEventsPollingEnabled: false,
  pollingForms: [],
  executingForms: [],
  formFillStartedFrom: null,
}

export const formsSlice = createSlice({
  name: 'forms',
  initialState,
  reducers: {
    setFormAiFeaturesLoading: (state, action: PayloadAction<boolean>) => {
      state.formAiFeaturesLoading = action.payload
    },
    setFormEventsForPollingLoading: (state, action: PayloadAction<boolean>) => {
      state.formEventsForPollingLoading = action.payload
    },
    setFormScorecardsLoading: (state, action: PayloadAction<boolean>) => {
      state.formScorecardsLoading = action.payload
    },
    setFormAiAgentResultsLoading: (state, action: PayloadAction<boolean>) => {
      state.formAiAgentResultsLoading = action.payload
    },
    startFormEventsPolling: (state) => {
      state.formEventsPollingEnabled = true
    },
    stopFormEventsPolling: (state) => {
      state.formEventsPollingEnabled = false
    },
    startFormPolling: (state, action: PayloadAction<string>) => {
      state.pollingForms = [...new Set([...state.pollingForms, action.payload])]
    },
    stopFormPolling: (state, action: PayloadAction<string>) => {
      state.pollingForms = state.pollingForms.filter((aiFeatureId) => aiFeatureId !== action.payload)
    },
    setFormAiFeatures: (state, action: PayloadAction<AiFeature[]>) => {
      formAiFeaturesAdapter.setAll(state.formAiFeatures, action.payload)
    },
    setFormScorecards: (state, action: PayloadAction<Scorecard[]>) => {
      formScorecardsAdapter.setAll(state.formScorecards, action.payload)
    },
    addFormAiAgentResults: (state, action: PayloadAction<AIAgentResult[]>) => {
      formAiAgentResultsAdapter.addMany(state.formAiAgentResults, action.payload)
    },
    setFormEventsForPolling: (state, action: PayloadAction<RoomEvent[]>) => {
      state.formEventsForPolling = action.payload
    },
    setFormAiAgentResult: (state, action: PayloadAction<AIAgentResult>) => {
      formAiAgentResultsAdapter.setOne(state.formAiAgentResults, action.payload)
    },
    removeFormAiAgentResults: (state, action: PayloadAction<string>) => {
      const keysToRemove = formAiAgentResultsAdapter
        .getSelectors()
        .selectIds(state.formAiAgentResults)
        .filter((resultKey) => getAiFeatureIdFromFormResultKey(resultKey) === action.payload)
      formAiAgentResultsAdapter.removeMany(state.formAiAgentResults, keysToRemove)
    },
    formExecuteStarted: (state, action: PayloadAction<string>) => {
      state.executingForms = [...new Set([...state.executingForms, action.payload])]
    },
    formExecuteFinished: (state, action: PayloadAction<string>) => {
      state.executingForms = state.executingForms.filter((aiFeatureId) => aiFeatureId !== action.payload)
    },
    setFormFillStartedFrom: (state, action: PayloadAction<string | null>) => {
      state.formFillStartedFrom = action.payload
    },
    resetForms: () => initialState,
  },
})

export const {
  setFormAiFeaturesLoading,
  setFormEventsForPollingLoading,
  setFormScorecardsLoading,
  setFormAiAgentResultsLoading,
  startFormEventsPolling,
  stopFormEventsPolling,
  startFormPolling,
  stopFormPolling,
  setFormAiFeatures,
  setFormScorecards,
  addFormAiAgentResults,
  setFormEventsForPolling,
  setFormAiAgentResult,
  removeFormAiAgentResults,
  formExecuteStarted,
  formExecuteFinished,
  resetForms,
  setFormFillStartedFrom,
} = formsSlice.actions

const formAiFeaturesSelectors = formAiFeaturesAdapter.getSelectors((state: RootState) => state.meetingSummary.forms.formAiFeatures)
const formScorecardsSelectors = formScorecardsAdapter.getSelectors((state: RootState) => state.meetingSummary.forms.formScorecards)
const formAiAgentResultsSelectors = formAiAgentResultsAdapter.getSelectors((state: RootState) => state.meetingSummary.forms.formAiAgentResults)

export const selectFormAiFeatures = (state: RootState) => formAiFeaturesSelectors.selectAll(state)

export const selectFormAiFeaturesForCurrentMeetingTemplate = createSelector(
  [(state: RootState) => formAiFeaturesSelectors.selectAll(state), selectSelectedMeetingTemplate],
  (formAiFeatures, selectedMeetingTemplate) => {
    if (!selectedMeetingTemplate) {
      return []
    }
    return formAiFeatures.filter((aiFeature) => selectedMeetingTemplate.formPresets.some((form) => form.aiFeatureId === aiFeature.id))
  },
)

export const selectFormScorecardForAiFeature = createSelector(
  [(state: RootState) => state, (_: RootState, aiFeatureId: string) => aiFeatureId],
  (state, aiFeatureId) => formScorecardsSelectors.selectAll(state).find((scorecard) => scorecard.aiFeatureIds[0] === aiFeatureId) ?? null,
)

export const selectFormsLoading = (state: RootState) =>
  state.meetingSummary.forms.formAiFeaturesLoading ||
  state.meetingSummary.forms.formEventsForPollingLoading ||
  state.meetingSummary.forms.formScorecardsLoading ||
  state.meetingSummary.forms.formAiAgentResultsLoading

export const selectFormEventsForPollingLoading = (state: RootState) => state.meetingSummary.forms.formEventsForPollingLoading

export const selectFormEventPollingEnabled = (state: RootState) => state.meetingSummary.forms.formEventsPollingEnabled

export const selectPollingForms = (state: RootState) => state.meetingSummary.forms.pollingForms

export const selectExecutingForms = (state: RootState) => state.meetingSummary.forms.executingForms

export const selectFormIsPolling = createSelector([selectPollingForms, (_: RootState, aiFeatureId: string) => aiFeatureId], (pollingForms, aiFeatureId) =>
  pollingForms.includes(aiFeatureId),
)

export const selectFormIsExecuting = createSelector([selectExecutingForms, (_: RootState, aiFeatureId: string) => aiFeatureId], (executingForms, aiFeatureId) =>
  executingForms.includes(aiFeatureId),
)

export const selectFormResults = (state: RootState) => formAiAgentResultsSelectors.selectAll(state)

export const selectFormEventsForPolling = (state: RootState) => state.meetingSummary.forms.formEventsForPolling

export const selectFormResultsByAiFeatureId = createSelector([selectFormResults], (formResults) => {
  return formResults.reduce((acc: { [key: string]: AIAgentResult[] }, aiAgentResult) => {
    const { resultKey } = aiAgentResult
    const aiFeatureId = getAiFeatureIdFromFormResultKey(resultKey)
    if (!aiFeatureId) {
      return acc
    }
    if (aiFeatureId in acc) {
      acc[aiFeatureId].push(aiAgentResult)
    } else {
      acc[aiFeatureId] = [aiAgentResult]
    }
    return acc
  }, {})
})

export const selectFormResultsForAiFeatureId = createSelector(
  [selectFormResultsByAiFeatureId, selectFormAiFeatures, (_: RootState, aiFeatureId: string) => aiFeatureId],
  (formResultsByAiFeatureId, formAiFeatures, aiFeatureId) => {
    const formAiFeature = formAiFeatures.find((f) => f.id === aiFeatureId)
    if (!formAiFeature || !(aiFeatureId in formResultsByAiFeatureId)) {
      return []
    }
    const formIsUserLevel = formAiFeature.generalConfig?.userLevel
    return formResultsByAiFeatureId[aiFeatureId].filter((r) => {
      const isMeetingLevelResult = getUserIdFromFormResultKey(r.resultKey) === MEETING_LEVEL_RESULT_USER_ID
      return isMeetingLevelResult !== formIsUserLevel
    })
  },
)

export const selectShouldPollFormEvents = createSelector(
  [selectFormEventsForPollingLoading, selectFormEventsForPolling],
  (eventsForPollingLoading, eventsForPolling) => {
    if (eventsForPollingLoading) {
      return false
    }
    const aiFeatureExecutionFinished = eventsForPolling.some((e) => e.type === 'aiFeatureExecutionFinished')
    if (!aiFeatureExecutionFinished) {
      return true
    }
    const sortedEvents = [...eventsForPolling].sort((event1, event2) => {
      const { timestamp: timestamp1 } = event1
      const { timestamp: timestamp2 } = event2
      return timestamp1.localeCompare(timestamp2)
    })
    const latestEvent = sortedEvents.pop()
    return !latestEvent || latestEvent.type === 'userAllocationUpdated'
  },
)

export const selectShouldPollFormResultsForAiFeatureId = createSelector(
  [
    selectExecutedAiFeatures,
    selectFormEventsForPolling,
    selectFormAiFeatures,
    selectFormResultsByAiFeatureId,
    selectExecutingForms,
    selectUsersWithClientRole,
    (_: RootState, aiFeatureId: string) => aiFeatureId,
  ],
  (executedAiFeatures, eventsForPolling, formAiFeatures, formResultsByAiFeatureId, executingForms, clients, aiFeatureId) => {
    const executing = executingForms.includes(aiFeatureId)
    const aiFeatureWasExecuted = executedAiFeatures.some((executedAiFeature) => executedAiFeature.id === aiFeatureId)
    const latestAiFeatureExecutionFinishedEvent = eventsForPolling
      .filter((e) => e.type === 'aiFeatureExecutionFinished')
      .reduce((prev: null | RoomEvent, current: RoomEvent) => (prev && prev.timestamp.localeCompare(current.timestamp) === 1 ? prev : current), null)
    if (!executing && (!aiFeatureWasExecuted || hasTenMinutesPassed(latestAiFeatureExecutionFinishedEvent?.timestamp))) {
      return false
    }

    const formResults = formResultsByAiFeatureId[aiFeatureId] ?? []
    const formAiFeature = formAiFeatures.find((f) => f.id === aiFeatureId)
    if (!formAiFeature) {
      return false
    }
    const formIsUserLevel = formAiFeature.generalConfig?.userLevel

    const validFormResults = formResults.filter((r) => {
      const isMeetingLevelResult = getUserIdFromFormResultKey(r.resultKey) === MEETING_LEVEL_RESULT_USER_ID
      return isMeetingLevelResult !== formIsUserLevel
    })
    if (!formIsUserLevel) {
      return !validFormResults.length
    } else {
      return !!clients.length && clients.some((client) => !validFormResults.some((r) => getUserIdFromFormResultKey(r.resultKey) === client.id))
    }
  },
)

const getAiFeatureIdFromFormResultKey = (resultKey: string) => {
  try {
    const parts = resultKey.split('|')
    return parts.length >= 3 ? parts[2] : null
  } catch {
    return null
  }
}

export const getUserIdFromFormResultKey = (result: string) => {
  const keyParts = result.split('|')
  if (keyParts.length < 4) {
    return null
  }
  return keyParts[keyParts.length - 1] ?? null
}

export const selectFormFillStartedFrom = (state: RootState) => state.meetingSummary.forms.formFillStartedFrom
